/**
 * FeedbackStyleSection Component
 * Main container for all feedback style settings.
 */
import React, { useState } from 'react';
import type { TeacherProfile, UpdateProfileRequest } from '../../types/memory';
import StyleMetricsCard from './StyleMetricsCard';
import FocusAreasEditor from './FocusAreasEditor';
import PhrasePatternsEditor from './PhrasePatternsEditor';
import ExampleFeedbackManager from './ExampleFeedbackManager';
import EditStyleModal from './EditStyleModal';

interface FeedbackStyleSectionProps {
  profile: TeacherProfile;
  onUpdateProfile: (updates: UpdateProfileRequest) => void;
  onAddExample: (text: string, category: string) => void;
  onRemoveExample: (index: number) => void;
  onReExtract: () => void;
  isLoading?: boolean;
}

const FeedbackStyleSection: React.FC<FeedbackStyleSectionProps> = ({
  profile,
  onUpdateProfile,
  onAddExample,
  onRemoveExample,
  onReExtract,
  isLoading = false,
}) => {
  const [isEditStyleModalOpen, setIsEditStyleModalOpen] = useState(false);

  const handleStyleSave = (tone: string, formality: string) => {
    onUpdateProfile({ tone, formality });
  };

  const handleFocusAreasUpdate = (focusAreas: string[]) => {
    onUpdateProfile({ focus_areas: focusAreas });
  };

  const handlePhrasePatternsUpdate = (phrasePatterns: string[]) => {
    onUpdateProfile({ phrase_patterns: phrasePatterns });
  };

  const handleAvoidsUpdate = (avoids: string[]) => {
    onUpdateProfile({ avoids });
  };

  return (
    <div className="space-y-6">
      {/* Page title */}
      <div>
        <h2 className="text-2xl font-semibold text-gray-900">
          Feedback Style Profile
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          Manage how the AI generates feedback suggestions based on your
          personal style
        </p>
      </div>

      {/* Style Overview */}
      <StyleMetricsCard
        tone={profile.tone}
        formality={profile.formality}
        directnessScore={profile.directness_score}
        avgCommentLength={profile.avg_comment_length}
        onEdit={() => setIsEditStyleModalOpen(true)}
      />

      {/* Focus Areas */}
      <FocusAreasEditor
        focusAreas={profile.focus_areas}
        onUpdate={handleFocusAreasUpdate}
      />

      {/* Common Phrases */}
      <PhrasePatternsEditor
        title="Common Phrases"
        phrases={profile.phrase_patterns}
        onUpdate={handlePhrasePatternsUpdate}
        variant="positive"
      />

      {/* Phrases to Avoid */}
      <PhrasePatternsEditor
        title="Phrases to Avoid"
        phrases={profile.avoids}
        onUpdate={handleAvoidsUpdate}
        variant="negative"
      />

      {/* Example Feedback Corpus */}
      <ExampleFeedbackManager
        examples={profile.example_feedback}
        onAddExample={onAddExample}
        onRemoveExample={onRemoveExample}
        onReExtract={onReExtract}
        isLoading={isLoading}
      />

      {/* Profile Info */}
      <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
        <div className="flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-gray-400"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <span>
            Profile initialized from:{' '}
            <strong className="text-gray-900">{profile.initialized_from}</strong>
          </span>
        </div>
        {profile.style_embedding && (
          <div className="mt-2 flex items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-green-500"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span>Style embedding generated for semantic matching</span>
          </div>
        )}
      </div>

      {/* Edit Style Modal */}
      <EditStyleModal
        isOpen={isEditStyleModalOpen}
        tone={profile.tone}
        formality={profile.formality}
        onSave={handleStyleSave}
        onClose={() => setIsEditStyleModalOpen(false)}
      />
    </div>
  );
};

export default FeedbackStyleSection;
